import { css } from "styled-system/css";
import { useEffect, useState } from "react";
import { AdminPageShell } from "@/components/admin/admin-page-shell";
import { LoadingFrame } from "@/components/ui/loading-frame";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";

type MarketFeatureRow = {
  id: string;
  name: string;
  category: string;
  marketStatus: "available" | "coming_soon";
  monthlyPrice: number;
};

type Status = {
  database: boolean;
  publicUrl: boolean;
  sessionSecret: boolean;
  sendgrid: boolean;
  twilio: boolean;
  mercadopago: boolean;
  cloudinary: boolean;
  upstash: boolean;
};

const items: { key: keyof Status; name: string; hint: string }[] = [
  { key: "database", name: "Base de datos", hint: "Almacenamiento principal de cuentas, turnos y configuración." },
  { key: "publicUrl", name: "Sesiones y acceso (URL pública)", hint: "Debe coincidir con el dominio desde el que entran los usuarios." },
  { key: "sessionSecret", name: "Sesiones y acceso (clave de firma)", hint: "Protege las cookies de inicio de sesión." },
  { key: "sendgrid", name: "Envío de correos", hint: "Confirmaciones y recordatorios por email." },
  { key: "twilio", name: "WhatsApp (Twilio)", hint: "Notificaciones al cliente con el número de la plataforma cuando lo habilités en cada cuenta." },
  { key: "mercadopago", name: "Pagos online", hint: "Señas y cobros desde el flujo de reserva." },
  { key: "cloudinary", name: "Imágenes y archivos", hint: "Avatares y cargas si usás multimedia en la app." },
  { key: "upstash", name: "Protección de APIs públicas", hint: "Límite de solicitudes para la página de reservas (recomendado en producción)." },
];

function flag(ok: boolean) {
  return ok ? "Configurado" : "No configurado";
}

function flagTone(ok: boolean): "success" | "danger" {
  return ok ? "success" : "danger";
}

export function AdminSettings() {
  const { refresh } = useAuth();
  const [st, setSt] = useState<Status | null>(null);
  const [feePct, setFeePct] = useState<string>("5");
  const [feeMsg, setFeeMsg] = useState<string | null>(null);
  const [feeBusy, setFeeBusy] = useState(false);
  const [marketFeatures, setMarketFeatures] = useState<MarketFeatureRow[] | null>(null);
  const [marketFeatureBusyId, setMarketFeatureBusyId] = useState<string | null>(null);
  const [priceDrafts, setPriceDrafts] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const r = await fetchWithSessionSync("/api/admin/integration-status", { credentials: "include" }, refresh);
      if (cancelled) return;
      if (!r.ok) return;
      setSt((await r.json()) as Status);
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const r = await fetchWithSessionSync("/api/admin/platform-settings", { credentials: "include" }, refresh);
      if (cancelled) return;
      if (!r.ok) return;
      const j = (await r.json()) as { marketplaceFeePercent: number };
      if (typeof j.marketplaceFeePercent === "number") {
        setFeePct(String(j.marketplaceFeePercent));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const r = await fetchWithSessionSync("/api/market/admin/features", { credentials: "include" }, refresh);
      if (cancelled || !r.ok) return;
      const j = (await r.json()) as { features: MarketFeatureRow[] };
      setMarketFeatures(j.features);
      setPriceDrafts(Object.fromEntries(j.features.map((f) => [f.id, String(f.monthlyPrice)])));
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  async function setMarketStatus(featureId: string, marketStatus: "available" | "coming_soon") {
    setMarketFeatureBusyId(featureId);
    const r = await fetchWithSessionSync(
      `/api/market/admin/features/${featureId}`,
      {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ marketStatus }),
      },
      refresh,
    );
    setMarketFeatureBusyId(null);
    if (!r.ok) return;
    setMarketFeatures((prev) =>
      prev ? prev.map((f) => (f.id === featureId ? { ...f, marketStatus } : f)) : prev,
    );
  }

  async function setMarketPrice(featureId: string) {
    const raw = priceDrafts[featureId] ?? "";
    const n = Number(raw.replace(",", "."));
    const current = marketFeatures?.find((f) => f.id === featureId)?.monthlyPrice ?? 0;
    if (!Number.isFinite(n) || n <= 0) {
      setPriceDrafts((prev) => ({ ...prev, [featureId]: String(current) }));
      return;
    }
    if (n === current) return;
    setMarketFeatureBusyId(featureId);
    const r = await fetchWithSessionSync(
      `/api/market/admin/features/${featureId}`,
      {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ monthlyPrice: n }),
      },
      refresh,
    );
    setMarketFeatureBusyId(null);
    if (!r.ok) {
      setPriceDrafts((prev) => ({ ...prev, [featureId]: String(current) }));
      return;
    }
    setMarketFeatures((prev) => (prev ? prev.map((f) => (f.id === featureId ? { ...f, monthlyPrice: n } : f)) : prev));
    setPriceDrafts((prev) => ({ ...prev, [featureId]: String(n) }));
  }

  async function saveFee() {
    setFeeMsg(null);
    const n = Number(String(feePct).replace(",", "."));
    if (!Number.isFinite(n) || n < 0 || n > 100) {
      setFeeMsg("Ingresá un porcentaje entre 0 y 100.");
      return;
    }
    setFeeBusy(true);
    const r = await fetchWithSessionSync(
      "/api/admin/platform-settings",
      {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ marketplaceFeePercent: n }),
      },
      refresh,
    );
    setFeeBusy(false);
    if (!r.ok) {
      setFeeMsg("No se pudo guardar.");
      return;
    }
    const j = (await r.json()) as { marketplaceFeePercent: number };
    setFeePct(String(j.marketplaceFeePercent));
    setFeeMsg("Comisión guardada.");
  }

  if (!st) {
    return (
      <AdminPageShell title="Plataforma">
        <LoadingFrame variant="light" minHeight={280} />
      </AdminPageShell>
    );
  }

  return (
    <AdminPageShell
      title="Plataforma"
      description="Estado de las integraciones según el entorno (no se muestran secretos). Sirve como checklist al desplegar."
    >
      <div className={css({ display: "grid", gap: "4", maxW: "3xl" })}>
        <section
          className={css({
            rounded: "xl",
            borderWidth: "1px",
            borderColor: "#e2e8f0",
            bg: "white",
            boxShadow: "0 24px 48px rgba(15, 23, 42, 0.06)",
            overflow: "hidden",
          })}
        >
          <div className={css({ px: "5", py: "4", borderBottomWidth: "1px", borderColor: "#e2e8f0", bg: "#f8fafc" })}>
            <h2 className={css({ fontWeight: "semibold", color: "#0f172a" })}>Comisión Mercado Pago (marketplace)</h2>
            <p className={css({ fontSize: "xs", color: "#64748b", mt: "1" })}>
              Porcentaje que retiene la plataforma en pagos con cuenta del profesional vinculada (split). Por defecto 5%; podés cambiarlo
              cuando quieras.
            </p>
          </div>
          <div className={css({ px: "5", py: "4", display: "flex", flexWrap: "wrap", gap: "3", alignItems: "flex-end" })}>
            <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
              <span className={css({ fontSize: "xs", color: "#64748b" })}>Porcentaje (%)</span>
              <input
                type="text"
                inputMode="decimal"
                value={feePct}
                onChange={(e) => setFeePct(e.target.value)}
                disabled={feeBusy}
                className={css({
                  w: "32",
                  h: "10",
                  px: "3",
                  rounded: "lg",
                  borderWidth: "1px",
                  borderColor: "#e2e8f0",
                  fontSize: "sm",
                })}
              />
            </label>
            <button
              type="button"
              disabled={feeBusy}
              onClick={() => void saveFee()}
              className={css({
                px: "4",
                py: "2.5",
                rounded: "lg",
                fontSize: "sm",
                fontWeight: "semibold",
                color: "white",
                bg: "#0f172a",
                border: "none",
                cursor: feeBusy ? "wait" : "pointer",
                opacity: feeBusy ? 0.7 : 1,
              })}
            >
              Guardar
            </button>
            {feeMsg ? (
              <span
                className={css({
                  fontSize: "sm",
                  color: feeMsg === "Comisión guardada." ? "green.700" : "red.700",
                })}
              >
                {feeMsg}
              </span>
            ) : null}
          </div>
        </section>

        <section
          className={css({
            rounded: "xl",
            borderWidth: "1px",
            borderColor: "#e2e8f0",
            bg: "white",
            boxShadow: "0 24px 48px rgba(15, 23, 42, 0.06)",
            overflow: "hidden",
          })}
        >
          <div className={css({ px: "5", py: "4", borderBottomWidth: "1px", borderColor: "#e2e8f0", bg: "#f8fafc" })}>
            <h2 className={css({ fontWeight: "semibold", color: "#0f172a" })}>Módulos de Market</h2>
            <p className={css({ fontSize: "xs", color: "#64748b", mt: "1" })}>
              Controla si un módulo está disponible para suscribirse o todavía en "Próximamente" (sin precio ni cobro, solo registro de interés).
              Es un cambio global: afecta a todos los usuarios.
            </p>
          </div>
          {marketFeatures === null ? (
            <div className={css({ p: "6" })}>
              <LoadingFrame variant="light" minHeight={80} />
            </div>
          ) : marketFeatures.length === 0 ? (
            <p className={css({ px: "5", py: "6", fontSize: "sm", color: "#94a3b8" })}>No hay módulos cargados.</p>
          ) : (
            <ul className={css({ divideY: "1px", divideColor: "#e2e8f0" })}>
              {marketFeatures.map((f) => (
                <li
                  key={f.id}
                  className={css({
                    px: "5",
                    py: "4",
                    display: "flex",
                    flexDir: { base: "column", sm: "row" },
                    gap: "3",
                    justifyContent: "space-between",
                    alignItems: { sm: "center" },
                  })}
                >
                  <div>
                    <p className={css({ fontWeight: "medium", color: "#0f172a" })}>{f.name}</p>
                    <p className={css({ fontSize: "xs", color: "#64748b", mt: "1" })}>{f.category}</p>
                  </div>
                  <div className={css({ display: "flex", alignItems: "center", gap: "2", flexWrap: "wrap" })}>
                    <div className={css({ display: "flex", alignItems: "center", gap: "1" })}>
                      <span className={css({ fontSize: "sm", color: "#64748b" })}>$</span>
                      <input
                        type="number"
                        min={1}
                        step="0.01"
                        value={priceDrafts[f.id] ?? String(f.monthlyPrice)}
                        disabled={marketFeatureBusyId === f.id}
                        onChange={(e) => setPriceDrafts((prev) => ({ ...prev, [f.id]: e.target.value }))}
                        onBlur={() => void setMarketPrice(f.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                        }}
                        aria-label={`Precio mensual de ${f.name}`}
                        className={css({
                          fontSize: "sm",
                          color: "#0f172a",
                          bg: "white",
                          borderWidth: "1px",
                          borderColor: "#e2e8f0",
                          rounded: "lg",
                          px: "3",
                          py: "2",
                          w: "28",
                          outline: "none",
                          _focus: { borderColor: "token(colors.brand.500)" },
                          _disabled: { opacity: 0.6, cursor: "wait" },
                        })}
                      />
                      <span className={css({ fontSize: "xs", color: "#94a3b8" })}>/mes</span>
                    </div>
                    <select
                      value={f.marketStatus}
                      disabled={marketFeatureBusyId === f.id}
                      onChange={(e) => void setMarketStatus(f.id, e.target.value as "available" | "coming_soon")}
                      className={css({
                        fontSize: "sm",
                        color: "#0f172a",
                        bg: "white",
                        borderWidth: "1px",
                        borderColor: "#e2e8f0",
                        rounded: "lg",
                        px: "3",
                        py: "2",
                        outline: "none",
                        cursor: marketFeatureBusyId === f.id ? "wait" : "pointer",
                      })}
                    >
                      <option value="available">Disponible</option>
                      <option value="coming_soon">Próximamente</option>
                    </select>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section
          className={css({
            rounded: "xl",
            borderWidth: "1px",
            borderColor: "#e2e8f0",
            bg: "white",
            boxShadow: "0 24px 48px rgba(15, 23, 42, 0.06)",
            overflow: "hidden",
          })}
        >
          <div className={css({ px: "5", py: "4", borderBottomWidth: "1px", borderColor: "#e2e8f0", bg: "#f8fafc" })}>
            <h2 className={css({ fontWeight: "semibold", color: "#0f172a" })}>Integraciones</h2>
            <p className={css({ fontSize: "xs", color: "#64748b", mt: "1" })}>Solo indicamos si cada pieza está configurada, sin mostrar claves.</p>
          </div>
          <ul className={css({ divideY: "1px", divideColor: "#e2e8f0" })}>
            {items.map((item) => (
              <li
                key={item.key}
                className={css({
                  px: "5",
                  py: "4",
                  display: "flex",
                  flexDir: { base: "column", sm: "row" },
                  gap: "3",
                  justifyContent: "space-between",
                  alignItems: { sm: "flex-start" },
                })}
              >
                <div>
                  <p className={css({ fontWeight: "medium", color: "#0f172a" })}>{item.name}</p>
                  <p className={css({ fontSize: "xs", color: "#64748b", mt: "1" })}>{item.hint}</p>
                </div>
                <span
                  className={css({
                    fontSize: "xs",
                    fontWeight: "semibold",
                    px: "2.5",
                    py: "1",
                    rounded: "md",
                    flexShrink: 0,
                    alignSelf: { base: "flex-start", sm: "center" },
                    bg: flagTone(st[item.key]) === "success" ? "green.100" : "red.100",
                    color: flagTone(st[item.key]) === "success" ? "green.800" : "red.800",
                  })}
                >
                  {flag(st[item.key])}
                </span>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </AdminPageShell>
  );
}
